import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Paper, 
  Chip, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Grid,
  Button,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import {
  School as SchoolIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  SortByAlpha as SortIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import WrongAnswerDialog from '../common/WrongAnswerDialog';

const WrongAnswerNotebook = ({ onStudy, onRemove, onSelectTerm }) => {
  const { 
    wrongAnswers, 
    isLoadingWrongAnswers, 
    handleRemoveWrongAnswer, 
    loadWrongAnswers,
    setSelectedSubject
  } = useApp();
  const [currentSubject, setCurrentSubject] = useState('all');
  const [sortOption, setSortOption] = useState('alphabetical');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItem, setExpandedItem] = useState(null);
  const theme = useTheme();

  // 컴포넌트가 마운트될 때 오답 데이터 로드
  useEffect(() => {
    console.log('🔖 오답 노트 컴포넌트 마운트');
    loadWrongAnswers();
  }, [loadWrongAnswers]);

  // 디버깅용 콘솔 로그 추가
  useEffect(() => {
    if (wrongAnswers.length > 0) {
      console.log('🔖 오답 노트 데이터 구조:', wrongAnswers[0]);
      
      // 키워드 배열 확인
      const keywordsIssue = wrongAnswers.some(item => !Array.isArray(item.keywords));
      if (keywordsIssue) {
        console.warn('⚠️ 일부 오답의 키워드가 배열이 아닙니다. 데이터 정규화 필요.');
      }
      
      // wrong_answer 필드 확인
      const wrongAnswerIssue = wrongAnswers.some(item => !item.wrong_answer && !item.note);
      if (wrongAnswerIssue) {
        console.warn('⚠️ 일부 오답의 wrong_answer 또는 note 필드가 없습니다.');
      }
    }
  }, [wrongAnswers]);

  // 과목 목록 생성 - 안정적인 정렬 적용
  const subjectsList = React.useMemo(() => {
    const subjects = Array.from(new Set(wrongAnswers.map(item => item.subject))).filter(Boolean);
    return subjects.sort((a, b) => a.localeCompare(b, 'ko'));
  }, [wrongAnswers]);

  // 정렬된 오답 목록 생성 - 안정적인 정렬과 키 생성
  const sortedAndFilteredAnswers = React.useMemo(() => {
    let filtered = currentSubject === 'all' 
      ? [...wrongAnswers]
      : wrongAnswers.filter(item => item.subject === currentSubject);

    // 검색어 필터링 적용
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => 
        (item.term && item.term.toLowerCase().includes(term)) ||
        (item.subject && item.subject.toLowerCase().includes(term)) ||
        (item.definition && item.definition.toLowerCase().includes(term)) ||
        ((item.wrong_answer || item.note) && (item.wrong_answer || item.note).toLowerCase().includes(term))
      );
    }

    // 각 항목에 안정적인 키 추가 및 데이터 정규화
    filtered = filtered.map((item, index) => {
      // 데이터 형식 일관성 유지
      const normalizedItem = {
        ...item,
        stableKey: `${item.id}-${index}`,
        // 필수 필드 보장
        wrong_answer: item.wrong_answer || '',
        note: item.wrong_answer || '',
        wrong_count: item.wrong_count || 1,
        // 추가 필드
        hint_level: item.hint_level || 0,
        keywords_matched: item.keywords_matched || 0,
        total_keywords: item.total_keywords || 0,
        score: item.score || 0
      };
      
      return normalizedItem;
    });

    switch (sortOption) {
      case 'random':
        // Fisher-Yates 알고리즘을 사용한 안정적인 셔플
        const shuffled = [...filtered];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      
      case 'subject':
        return filtered.sort((a, b) => {
          const subjectCompare = (a.subject || '').localeCompare(b.subject || '', 'ko');
          return subjectCompare !== 0 ? subjectCompare : a.stableKey.localeCompare(b.stableKey);
        });
      
      case 'score':
        // 점수 높은 순으로 정렬 (새로 추가)
        return filtered.sort((a, b) => {
          const scoreCompare = (b.score || 0) - (a.score || 0);
          return scoreCompare !== 0 ? scoreCompare : a.stableKey.localeCompare(b.stableKey);
        });
      
      case 'alphabetical':
      default:
        return filtered.sort((a, b) => {
          const termCompare = (a.term || '').localeCompare(b.term || '', 'ko');
          return termCompare !== 0 ? termCompare : a.stableKey.localeCompare(b.stableKey);
        });
    }
  }, [wrongAnswers, currentSubject, sortOption, searchTerm]);

  const handleStudy = async (subject) => {
    try {
      setIsLoading(true);
      
      // onStudy prop이 없는 경우 내부적으로 처리
      if (typeof onStudy === 'function') {
        await onStudy(subject);
      } else {
        // 내부 처리 - QuizContext로 이동하거나 알림만 표시
        console.log('📚 학습 시작: ', subject);
        setSelectedSubject(subject);
      }
    } catch (error) {
      console.error('학습 시작 중 오류 발생:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubjectChange = (event) => {
    setCurrentSubject(event.target.value);
  };

  const handleDeleteWrongAnswer = async (answer) => {
    try {
      if (typeof onRemove === 'function') {
        await onRemove(answer);
      } else {
        await handleRemoveWrongAnswer(answer.id);
      }
    } catch (error) {
      console.error('오답 삭제 중 오류 발생:', error);
    }
  };

  const handleSortChange = (event) => {
    setSortOption(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleTermClick = (answer) => {
    if (typeof onSelectTerm === 'function') {
      onSelectTerm(answer);
    } else {
      console.log('용어 선택:', answer.term);
    }
  };

  const handleExpandToggle = (id, event) => {
    event.stopPropagation();
    setExpandedItem(expandedItem === id ? null : id);
  };

  if (isLoadingWrongAnswers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          오답 노트 {sortedAndFilteredAnswers.length > 0 && `(${sortedAndFilteredAnswers.length}개)`}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="subject-filter-label">과목 필터</InputLabel>
            <Select
              labelId="subject-filter-label"
              value={currentSubject}
              label="과목 필터"
              onChange={handleSubjectChange}
              startAdornment={
                <InputAdornment position="start">
                  <FilterIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="all">전체 과목</MenuItem>
              {subjectsList.map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="sort-option-label">정렬 방식</InputLabel>
            <Select
              labelId="sort-option-label"
              value={sortOption}
              label="정렬 방식"
              onChange={handleSortChange}
              startAdornment={
                <InputAdornment position="start">
                  <SortIcon fontSize="small" />
                </InputAdornment>
              }
            >
              <MenuItem value="alphabetical">단어순</MenuItem>
              <MenuItem value="subject">과목순</MenuItem>
              <MenuItem value="score">점수순</MenuItem>
              <MenuItem value="random">랜덤</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            placeholder="검색어를 입력하세요 (용어, 정의, 키워드)"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {sortedAndFilteredAnswers.length > 0 ? (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                <TableCell width="25%"><Typography fontWeight="bold">용어</Typography></TableCell>
                <TableCell width="15%"><Typography fontWeight="bold">분야</Typography></TableCell>
                <TableCell width="50%"><Typography fontWeight="bold">정의</Typography></TableCell>
                <TableCell width="10%" align="center"><Typography fontWeight="bold">관리</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredAnswers.map((answer) => (
                <React.Fragment key={answer.stableKey || answer.id}>
                  <TableRow 
                    hover
                    onClick={() => handleTermClick(answer)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography fontWeight="medium">{answer.term || '제목 없음'}</Typography>
                      {answer.wrong_count > 1 && (
                        <Chip 
                          label={`틀린 횟수: ${answer.wrong_count}`} 
                          size="small" 
                          color="error" 
                          variant="outlined"
                          sx={{ mt: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={answer.subject || '과목 없음'} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}>
                        {answer.definition || '정답 없음'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={expandedItem === answer.id ? "접기" : "펼치기"}>
                        <IconButton 
                          size="small"
                          onClick={(e) => handleExpandToggle(answer.id, e)}
                        >
                          {expandedItem === answer.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="삭제">
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteWrongAnswer(answer);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell 
                      colSpan={4} 
                      sx={{ 
                        p: 0,
                        borderBottom: expandedItem === answer.id ? '1px solid rgba(224, 224, 224, 1)' : 'none' 
                      }}
                    >
                      <Collapse in={expandedItem === answer.id} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, backgroundColor: 'rgba(0, 0, 0, 0.01)' }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              정답
                            </Typography>
                            <Typography variant="body1">
                              {answer.definition || '정답 없음'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              내 답변
                            </Typography>
                            <Typography variant="body1" color="error.main">
                              {answer.wrong_answer || answer.note || '없음'}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              채점 정보
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                              <Chip 
                                label={`점수: ${answer.score || 0}점`} 
                                size="small" 
                                color={
                                  (answer.score || 0) >= 70 ? "success" :
                                  (answer.score || 0) >= 40 ? "warning" : "error"
                                }
                              />
                              <Chip 
                                label={`키워드: ${answer.keywords_matched || 0}/${answer.total_keywords || 0}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {answer.hint_level > 0 && (
                                <Chip
                                  label={`힌트 레벨: ${answer.hint_level}`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }} elevation={0} variant="outlined">
          <Typography variant="body1" color="text.secondary">
            {searchTerm
              ? '검색 결과가 없습니다.'
              : currentSubject === 'all' 
                ? '저장된 오답이 없습니다.'
                : `'${currentSubject}' 과목에 대한 오답이 없습니다.`}
          </Typography>
          {searchTerm && (
            <Button 
              variant="outlined" 
              color="primary" 
              sx={{ mt: 2 }}
              onClick={() => setSearchTerm('')}
            >
              검색 초기화
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WrongAnswerNotebook; 